<template>
	<div class="flex flex-col grow h-full min-h-0">
		<div class="flex flex-row z-20" :class="formView ? 'sticky top-[4.8125rem]' : 'shrink-0'">
			<div
				v-for="tab in tabs"
				:key="tab"
				class="form-tab"
				:class="tabClass(tab)"
				@click.prevent="() => setActiveTab(tab)"
			>
        <span>
          {{ tab }}
        </span>
				<AppIcon
					v-if="tabErrors[tab]"
					class="absolute right-5 top-3 text-red-500"
					icon="fa-square-exclamation"
					size="lg"
					bounce
					style="--fa-animation-iteration-count: 1"
				/>
			</div>
		</div>
		<div
			class="flex flex-col flex-wrap"
			:class="formView ? 'bg-white border border-t-0 border-b-0 p-8 min-h-[80%]' : 'grow min-h-0'"
			:style="formView ? 'height: calc(100% - 2rem);' : undefined"
		>
			<div
				v-for="tab in tabs"
				v-show="tab === activeTab"
				:key="tab"
				ref="contentBlocks"
				:name="tab"
				:class="`flex flex-col grow shrink min-h-0 ${tab}-tab-contents`"
			>
				<slot :name="tab"/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
const props = withDefaults(
	defineProps<{
		tabs: string[];
		initialTab?: string;
		formView?: boolean;
	}>(),
	{
		formView: true,
	},
);

const { tabs, initialTab } = toRefs(props);
const activeTab = ref<string>(initialTab.value || tabs.value[0]);
const contentBlocks = ref<HTMLDivElement[] | []>([]);
const tabErrors = ref<Record<string, boolean>>(Object.fromEntries(tabs.value.map((tab) => [tab, false])));

const tabClass = computed(() => (tab: string) => ({
	"form-tab--active": activeTab.value === tab,
	"form-tab--inactive": activeTab.value !== tab,
	"form-tab--error": tabErrors.value[tab],
}));

const { pause, resume } = useIntervalFn(
	() => {
		contentBlocks.value.forEach((block) => {
			const blockName = block.getAttribute("name")!;
			const invalid = block.querySelectorAll("[data-invalid]").length;
			tabErrors.value[blockName] = !!invalid;
		});
	},
	500,
	{
		immediate: true,
		immediateCallback: true,
	},
);

onActivated(() => {
	resume();
});

onDeactivated(() => {
	pause();
});

defineExpose({
	activeTab,
	setActiveTab,
});

function setActiveTab(tab: string) {
	activeTab.value = tab;
}

// function nextStep() {
//   const nextTab = props.tabs.indexOf(activeTab.value) + 1;
//   if (nextTab < props.tabs.length) {
//     setActiveTab(props.tabs[nextTab]);
//   }
// }

// function previousStep() {
//   const previousTab = props.tabs.indexOf(activeTab.value) - 1;
//   if (previousTab >= 0) {
//     setActiveTab(props.tabs[previousTab]);
//   }
// }
</script>

<style scoped>
.form-tab {
	@apply p-3 text-sm border grow cursor-pointer bg-gray-100 text-center text-gray-400 select-none relative font-bold;

	&:not(:first-child) {
		@apply border-l-0;
	}
}

.form-tab--active {
	@apply bg-white border-b-0 text-primary;
}

.form-tab--error {
	@apply text-red-500 font-bold;
}

.form-tab--inactive {
	@apply hover:bg-gray-50;
}
</style>
